import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig, CustomNavigationClient, clearMsalCacheForce, clearMsalInteractionCache } from './src/authConfig';
import { seedDatabase } from './src/seedData';
import { ErrorBoundary } from './src/components/ErrorBoundary';

// Exponer la función reparada en la consola para inyección manual
(window as any).seedDatabase = seedDatabase;

// Detector de versión de frontend
const APP_VERSION = '1.5.32';
const initVersionCheck = async (): Promise<boolean> => {
  try {
    const oldVersion = localStorage.getItem('klydapp_app_version');
    if (oldVersion !== APP_VERSION) {
      console.log(`K-LyDapp version changed: ${oldVersion || 'none'} → ${APP_VERSION}`);
      
      if (window.caches) {
        try {
          const keys = await window.caches.keys();
          await Promise.all(keys.map(key => window.caches.delete(key)));
        } catch (e) {
          console.error('Error clearing Cache Storage during version update:', e);
        }
      }
      
      localStorage.setItem('klydapp_app_version', APP_VERSION);
      
      if (oldVersion !== null) {
        window.location.reload();
        return true;
      }
    }
  } catch (e) {
    console.error('Error in version detection:', e);
  }
  return false;
};

const versionCheckPromise = initVersionCheck();

import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

const msalInstance = new PublicClientApplication(msalConfig);
msalInstance.setNavigationClient(new CustomNavigationClient());

// Promesa global de inicialización de MSAL
const msalInitPromise = msalInstance.initialize();

if (Capacitor.isNativePlatform()) {
    Browser.addListener('browserFinished', () => {
        console.log("[MSAL Mobile] Browser terminado/cerrado por el usuario (Global). Limpiando caché y UI.");
        clearMsalInteractionCache();
        window.dispatchEvent(new CustomEvent('msal-browser-closed'));
    });
}

// Función de sanitización de URL para logs de auditoría seguros
const sanitizeUrlForLog = (url: string) => {
    try {
        const urlObj = new URL(url.replace('klydapp://', 'http://localhost/'));
        const params = new URLSearchParams(urlObj.search || urlObj.hash.substring(1));
        if (params.has('code')) params.set('code', '***CODE_REDACTED***');
        if (params.has('session_state')) params.set('session_state', '***SESSION_STATE_REDACTED***');
        if (params.has('id_token')) params.set('id_token', '***TOKEN_REDACTED***');
        if (params.has('access_token')) params.set('access_token', '***TOKEN_REDACTED***');
        if (params.has('token')) params.set('token', '***TOKEN_REDACTED***');
        return url.split('?')[0].split('#')[0] + '?' + params.toString();
    } catch (e) {
        return url.substring(0, 40) + '... (error de parsing)';
    }
};

// clearMsalCacheForce se importa desde authConfig.ts

if (Capacitor.isNativePlatform()) {
    localStorage.setItem('isNative', 'true');
    CapacitorApp.addListener('appUrlOpen', async (event) => {
        localStorage.setItem('isNative', 'true');
        console.log("[MSAL Mobile] Evento appUrlOpen gatillado con URL:", sanitizeUrlForLog(event.url));
        
        if (event.url.includes('k-lydapp.ldconstructora.cl') || event.url.startsWith('klydapp://')) {
            console.log("[MSAL Mobile] Cerrando navegador intermedio (In-App Browser)...");
            Browser.close().catch(console.error);
            
            // Esperar que la inicialización global de MSAL esté lista para evitar carreras
            console.log("[MSAL Mobile] Esperando inicialización de MSAL...");
            await msalInitPromise;
            console.log("[MSAL Mobile] MSAL Inicializado en appUrlOpen.");
            
            // 1. MSAL Handling
            let hashPart = "";
            if (event.url.includes("#")) {
                hashPart = "#" + event.url.split("#")[1];
            } else if (event.url.includes("?")) {
                hashPart = "#" + event.url.split("?")[1];
            }
            
            if (hashPart && (hashPart.includes("code=") || hashPart.includes("error="))) {
                console.log("[MSAL Mobile] Detectado hash/query de respuesta MSAL:", hashPart.split('&')[0] + "...");
                
                msalInstance.handleRedirectPromise({ hash: hashPart, navigateToLoginRequestUrl: false })
                    .then((response) => {
                        if (response && response.account) {
                            console.log("[MSAL Mobile] Sesión MSAL completada con éxito para el usuario:", response.account.username);
                            msalInstance.setActiveAccount(response.account);
                        } else {
                            console.warn("[MSAL Mobile] handleRedirectPromise resolvió pero sin cuenta activa. Limpiando caché por seguridad.");
                            clearMsalCacheForce();
                        }
                    })
                    .catch((error) => {
                        console.error("[MSAL Mobile] Error procesando redirect de MSAL:", error);
                        clearMsalInteractionCache();
                        
                        const isCancel = error.message?.includes("access_denied") || 
                                         error.message?.includes("user_cancelled") || 
                                         error.errorCode?.includes("access_denied") ||
                                         error.errorCode?.includes("user_cancelled");
                                         
                        if (!isCancel) {
                            alert("Error de autenticación con Microsoft: " + (error.message || error) + "\n\nPor favor, intenta iniciar sesión nuevamente.");
                        } else {
                            console.log("[MSAL Mobile] El usuario canceló la autenticación.");
                        }
                        
                        window.dispatchEvent(new CustomEvent('msal-browser-closed'));
                    });
            } else {
                console.log("[MSAL Mobile] No se detectó code ni error en el hash/query del deep link para MSAL.");
            }

            // 2. Supabase Magic Link & Universal Authentication Links Handling
            const isAuthUrl = 
                event.url.includes('/auth/invite') ||
                event.url.includes('/auth/otp') ||
                event.url.includes('/auth/callback') ||
                event.url.includes('/auth/confirm') ||
                event.url.includes('/auth-callback') ||
                event.url.startsWith('klydapp://auth');

            if (isAuthUrl) {
                try {
                    let token = null;
                    let type = 'magiclink';
                    
                    if (event.url.startsWith('klydapp://')) {
                        // Extract query params manually since URL() might fail on custom schemes
                        const queryString = event.url.split('?')[1];
                        if (queryString) {
                            const params = new URLSearchParams(queryString);
                            token = params.get('token');
                            type = params.get('type') || 'magiclink';
                        }
                    } else {
                        const urlObj = new URL(event.url);
                        token = urlObj.searchParams.get('token');
                        
                        // Deduce default authentication type from universal path prefix if not provided in query
                        const explicitType = urlObj.searchParams.get('type');
                        if (explicitType) {
                            type = explicitType;
                        } else {
                            const path = urlObj.pathname;
                            if (path.includes('/invite')) type = 'invite';
                            else if (path.includes('/otp')) type = 'magiclink';
                            else if (path.includes('/callback')) type = 'magiclink';
                            else if (path.includes('/confirm')) type = 'signup';
                            else type = 'magiclink';
                        }
                    }
                    
                    if (token) {
                        const { supabase } = await import('./src/supabaseClient');
                        const { error } = await (supabase.auth as any).verifyOtp({ token_hash: token, type: type });
                        if (error) {
                            console.error("Error verifying OTP from deep link:", error);
                            alert("El enlace de acceso ha expirado o ya fue utilizado. Por favor, solicita uno nuevo.");
                        }
                    }
                } catch (e) {
                    console.error("Error parsing auth link:", e);
                }
            } else if (event.url.includes('#access_token=')) {
                try {
                    const hashPart = event.url.split('#')[1];
                    const params = new URLSearchParams(hashPart);
                    const access_token = params.get('access_token');
                    const refresh_token = params.get('refresh_token');
                    
                    if (access_token && refresh_token) {
                        const { supabase } = await import('./src/supabaseClient');
                        await supabase.auth.setSession({ access_token, refresh_token });
                    }
                } catch (e) {
                    console.error("Error setting Supabase session from deep link:", e);
                }
            }
        }
    });
}

msalInitPromise.then(() => {
    versionCheckPromise.then((didReload) => {
        if (didReload) {
            console.log("Version check triggered reload, aborting React mount.");
            return;
        }

        // En plataformas móviles nativas, handleRedirectPromise() no debe llamarse en el arranque general
        // porque al no haber hash en window.location (URL local del WebView) se limpia la caché temporal/PKCE
        // antes de que Capacitor dispare el evento 'appUrlOpen'.
        const handleRedirect = Capacitor.isNativePlatform() 
            ? Promise.resolve(null) 
            : msalInstance.handleRedirectPromise();

        handleRedirect.then((response) => {
            if (response && response.account) {
                msalInstance.setActiveAccount(response.account);
            } else {
                // Fallback: Check if there's already an active account in cache
                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    msalInstance.setActiveAccount(accounts[0]);
                }
            }

            const rootElement = document.getElementById('root');
            if (!rootElement) {
              throw new Error("Could not find root element to mount to");
            }

            const root = ReactDOM.createRoot(rootElement);
            root.render(
              <React.StrictMode>
                <ErrorBoundary>
                  <MsalProvider instance={msalInstance}>
                    <App />
                  </MsalProvider>
                </ErrorBoundary>
              </React.StrictMode>
            );
        }).catch(error => {
            console.error("Error in MSAL redirect:", error);
            
            // Render anyway to show potential Unauthenticated state
            const rootElement = document.getElementById('root');
            if (rootElement) {
                const root = ReactDOM.createRoot(rootElement);
                root.render(
                  <React.StrictMode>
                    <ErrorBoundary>
                      <MsalProvider instance={msalInstance}>
                        <App />
                      </MsalProvider>
                    </ErrorBoundary>
                  </React.StrictMode>
                );
            }
        });
    });
});